'use client';

import React from 'react';
import Image from '@/components/Image';
import { EPlatform } from '@/utils/enums/platform-enum';
import { RelatedCategories as RelatedCategoriesType } from '../types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
// @ts-ignore: Icon library missing type definitions
import { ToRightArrow } from '@zeelool/icon';
import './related-categories.css';
import MagicJump from '@/components/MagicJump';
import { useFrenchSummerTracking } from '../hooks/useFrenchSummerTracking';
import { useInView } from 'react-intersection-observer';

interface RelatedCategoriesProps {
  data: RelatedCategoriesType;
  deviceType: EPlatform;
}

function RelatedCategoryCard({
  item,
  index,
  isWeb,
  isAverage,
  trackAdClick,
  trackAdExp,
}: {
  item: any;
  index: number;
  isWeb: boolean;
  isAverage: boolean;
  trackAdClick: any;
  trackAdExp: any;
}) {
  const imageResource = isWeb ? item.pc_image : item.wap_image;

  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  React.useEffect(() => {
    if (inView) {
      trackAdExp(
        '往期系列合集',
        '',
        index,
        item.jump_content?.type || '',
        item.jump_content?.content || '',
      );
    }
  }, [inView, index, item, trackAdExp]);

  if (!imageResource) return null;

  const handleCardClick = () => {
    trackAdClick(
      '往期系列合集',
      '',
      index,
      item.jump_content?.type || '',
      item.jump_content?.content || '',
    );
  };

  const CardContent = (
    <div
      className="w-full group cursor-pointer rounded-[8px] overflow-hidden relative"
      onClick={handleCardClick}
    >
      <div
        className="w-full relative bg-gray-100 overflow-hidden"
        style={{
          ...(isAverage && isWeb
            ? {
                maxHeight: '315px',
                height: '315px',
              }
            : {
                aspectRatio: imageResource.aspectRatio
                  ? imageResource.aspectRatio.replace('/', ' / ')
                  : isWeb
                    ? '420/315'
                    : '600/450',
              }),
        }}
      >
        <Image
          src={imageResource.url}
          alt={item.title}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[43px] md:h-[54px] flex items-center justify-center bg-black/10 z-10 transition-colors duration-300 group-hover:bg-black/20">
        <h3 className="text-[14px] md:text-[18px] text-white line-clamp-1 px-[10px] text-600">
          {item.title}
        </h3>
      </div>
      {item.date && (
        <div className="absolute top-[8px] right-[8px] md:top-[12px] md:right-[12px] z-10 bg-white/60 rounded-[4px] px-[6px] py-[4px] md:px-[8px] md:py-[6px] flex items-center justify-center">
          <span className="font-bb text-[12px] md:text-[14px] text-black">{item.date}</span>
        </div>
      )}
    </div>
  );

  return (
    <div ref={ref} className="w-full h-full block">
      {item.jump_content ? (
        <MagicJump item={item.jump_content} className="block w-full h-full">
          {CardContent}
        </MagicJump>
      ) : (
        CardContent
      )}
    </div>
  );
}

export default function RelatedCategories({ data, deviceType }: RelatedCategoriesProps) {
  const isWeb = deviceType === EPlatform.WEB;
  // 为组件实例生成唯一ID
  const uniqueId = React.useMemo(() => `related-categories-swiper`, []);

  const [isBeginning, setIsBeginning] = React.useState(true);
  const [isEnd, setIsEnd] = React.useState(false);
  const swiperRef = React.useRef<any>(null);

  const { trackAdClick, trackAdExp } = useFrenchSummerTracking();

  const isAverage = data?.list && data.list.length < 4;

  const swiperOptions = React.useMemo(
    () => ({
      modules: [FreeMode, Navigation],
      spaceBetween: isWeb ? 24 : 12,
      slidesPerView: isAverage && isWeb ? data.list.length : ('auto' as const),
      freeMode: true,
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      navigation: isWeb
        ? {
            nextEl: `.${uniqueId}-next`,
            prevEl: `.${uniqueId}-prev`,
          }
        : false,
    }),
    [isWeb, uniqueId, isAverage, data?.list?.length],
  );

  if (!data || !data.list || data.list.length < 2) return null;

  return (
    <div className="w-full py-[40px] md:py-[60px] lg:py-[80px] bg-[#eeeeee] overflow-hidden">
      <div className="max-w-[1920px] mx-auto px-[8px] md:px-[40px] lg:px-[90px]">
        {data.title && (
          <h2 className="font-bb text-[24px] md:text-[32px] lg:text-[40px] text-[#282828] text-center mb-[30px] md:mb-[50px]">
            {data.title}
          </h2>
        )}

        <div className="relative product-swiper-container group/swiper">
          <Swiper
            ref={swiperRef}
            {...swiperOptions}
            onSlideChange={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            onInit={(swiper) => {
              setIsBeginning(swiper.isBeginning);
              setIsEnd(swiper.isEnd);
            }}
            className="related-categories-swiper !pb-[20px]"
          >
            {data.list.map((item, index) => {
              return (
                <SwiperSlide
                  key={index}
                  className={isAverage && isWeb ? '' : '!w-[280px] md:!w-[360px] lg:!w-[420px]'}
                >
                  <RelatedCategoryCard
                    item={item}
                    index={index}
                    isWeb={isWeb}
                    isAverage={isAverage}
                    trackAdClick={trackAdClick}
                    trackAdExp={trackAdExp}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>

          {/* PC端导航按钮 */}
          {isWeb && data.list.length > 3 && (
            <>
              <div
                className={`
                ${uniqueId}-next product-swiper-navigation absolute z-10 flex items-center justify-center
                w-[75px] lg:w-[100px] h-[53px] lg:h-[70px]
                top-[50%] right-[0] lg:right-[0]
                rounded-[35px] border-2 border-white text-white cursor-pointer
                ${isEnd ? '!hidden' : ''}
              `}
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                }}
                onClick={() => {
                  swiperRef.current?.swiper?.slideNext();
                }}
              >
                <ToRightArrow className="text-[30px] lg:text-[40px]" />
              </div>
              <div
                className={`
                ${uniqueId}-prev product-swiper-navigation absolute z-10 flex items-center justify-center
                w-[75px] lg:w-[100px] h-[53px] lg:h-[70px]
                top-[50%] left-[0] lg:left-[0]
                rounded-[35px] border-2 border-white text-white cursor-pointer
                ${isBeginning ? '!hidden' : ''}
              `}
                style={{
                  backgroundColor: 'rgba(0, 0, 0, 0.4)',
                }}
                onClick={() => {
                  swiperRef.current?.swiper?.slidePrev();
                }}
              >
                <ToRightArrow className="text-[30px] lg:text-[40px] rotate-180" />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
